/**
 * Screen Storage Utility
 * Manages namespaced localStorage for multiple screen/masjid permutations
 * Allows the same browser to run different screen configurations in separate tabs
 */

export interface ScreenData {
  screenCode: string;
  masjidId: string;
  masjidName?: string;
  lastAuthenticated?: string;
}

// Session storage key for current tab's screen identifier
const CURRENT_SCREEN_KEY = "currentScreenIdentifier";

/**
 * Generate a unique storage key for a screen/masjid combination
 */
export function generateScreenKey(
  screenCode: string,
  masjidId: string
): string {
  return `screen_${screenCode}_${masjidId}`;
}

/**
 * Get the current screen identifier from sessionStorage (tab-specific)
 * Falls back to URL params if not set
 */
export function getCurrentScreenIdentifier(): string | null {
  // First check sessionStorage (persists for this tab only)
  const sessionIdentifier = sessionStorage.getItem(CURRENT_SCREEN_KEY);
  if (sessionIdentifier) {
    return sessionIdentifier;
  }

  // Fall back to URL params
  const params = new URLSearchParams(window.location.search);
  const screenCode = params.get("screenCode") || params.get("screen");
  const masjidId = params.get("masjidId") || params.get("masjid");

  if (screenCode && masjidId) {
    const key = generateScreenKey(screenCode, masjidId);
    // Store in sessionStorage for this tab
    sessionStorage.setItem(CURRENT_SCREEN_KEY, key);
    return key;
  }

  return null;
}

/**
 * Set the current screen identifier for this tab
 */
export function setCurrentScreenIdentifier(
  screenCode: string,
  masjidId: string
): string {
  const key = generateScreenKey(screenCode, masjidId);
  sessionStorage.setItem(CURRENT_SCREEN_KEY, key);
  return key;
}

/**
 * Clear the current screen identifier for this tab
 */
export function clearCurrentScreenIdentifier(): void {
  sessionStorage.removeItem(CURRENT_SCREEN_KEY);
}

/**
 * Save screen data to localStorage with namespaced key
 */
export function saveScreenData(data: ScreenData): void {
  const key = generateScreenKey(data.screenCode, data.masjidId);

  // Store the full data object
  localStorage.setItem(key, JSON.stringify(data));

  // Also set the current screen identifier for this tab
  setCurrentScreenIdentifier(data.screenCode, data.masjidId);

  // For backward compatibility, also store individual keys
  // These will be prefixed with the screen key
  localStorage.setItem(`${key}_screenCode`, data.screenCode);
  localStorage.setItem(`${key}_masjidId`, data.masjidId);
  if (data.masjidName) {
    localStorage.setItem(`${key}_masjidName`, data.masjidName);
  }
}

/**
 * Get screen data for the current tab's screen
 */
export function getScreenData(): ScreenData | null {
  const key = getCurrentScreenIdentifier();
  if (!key) return null;

  const data = localStorage.getItem(key);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Get screen data by specific screen code and masjid ID
 */
export function getScreenDataByKey(
  screenCode: string,
  masjidId: string
): ScreenData | null {
  const key = generateScreenKey(screenCode, masjidId);
  const data = localStorage.getItem(key);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Clear screen data for the current tab
 */
export function clearScreenData(): void {
  const key = getCurrentScreenIdentifier();
  if (key) {
    // Remove the main data object
    localStorage.removeItem(key);

    // Remove individual keys
    localStorage.removeItem(`${key}_screenCode`);
    localStorage.removeItem(`${key}_masjidId`);
    localStorage.removeItem(`${key}_masjidName`);

    // Clear all keys that start with this screen's prefix
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (storageKey?.startsWith(key)) {
        keysToRemove.push(storageKey);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  }

  // Clear the session identifier
  clearCurrentScreenIdentifier();
}

/**
 * Check if the current tab has an authenticated screen
 */
export function isScreenAuthenticated(): boolean {
  const data = getScreenData();
  return !!(data?.screenCode && data?.masjidId);
}

/**
 * Get the current screen code for this tab
 */
export function getCurrentScreenCode(): string | null {
  const data = getScreenData();
  return data?.screenCode || null;
}

/**
 * Get the current masjid ID for this tab
 */
export function getCurrentMasjidId(): string | null {
  const data = getScreenData();
  return data?.masjidId || null;
}

/**
 * Get a namespaced storage key for the current screen
 * Use this for storing additional data related to the current screen
 */
export function getNamespacedKey(suffix: string): string | null {
  const key = getCurrentScreenIdentifier();
  if (!key) return null;
  return `${key}_${suffix}`;
}

/**
 * Get a value from namespaced storage
 */
export function getNamespacedValue(suffix: string): string | null {
  const key = getNamespacedKey(suffix);
  if (!key) return null;
  return localStorage.getItem(key);
}

/**
 * Set a value in namespaced storage
 */
export function setNamespacedValue(suffix: string, value: string): void {
  const key = getNamespacedKey(suffix);
  if (key) {
    localStorage.setItem(key, value);
  }
}

/**
 * Get all stored screen configurations (useful for debugging)
 */
export function getAllStoredScreens(): ScreenData[] {
  const screens: ScreenData[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key?.startsWith("screen_") &&
      !key.includes("_screenCode") &&
      !key.includes("_masjidId") &&
      !key.includes("_masjidName")
    ) {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          if (parsed.screenCode && parsed.masjidId) {
            screens.push(parsed);
          }
        }
      } catch {
        // Skip invalid entries
      }
    }
  }
  return screens;
}

/**
 * Migration helper: Convert old single-screen storage to new namespaced format
 * Call this once on app startup to migrate existing users
 */
export function migrateOldStorage(): boolean {
  const oldScreenCode = localStorage.getItem("screenCode");
  const oldMasjidId = localStorage.getItem("masjidId");

  if (oldScreenCode && oldMasjidId) {
    // Check if already migrated
    const newKey = generateScreenKey(oldScreenCode, oldMasjidId);
    if (localStorage.getItem(newKey)) {
      // Already migrated, clean up old keys
      localStorage.removeItem("screenCode");
      localStorage.removeItem("masjidId");
      return false;
    }

    // Migrate to new format
    const oldMasjidName = localStorage.getItem(`${oldScreenCode}_masjidName`);
    saveScreenData({
      screenCode: oldScreenCode,
      masjidId: oldMasjidId,
      masjidName: oldMasjidName || undefined,
      lastAuthenticated: new Date().toISOString(),
    });

    // Remove old keys
    localStorage.removeItem("screenCode");
    localStorage.removeItem("masjidId");

    console.log(
      `[ScreenStorage] Migrated old storage to new format: ${newKey}`
    );
    return true;
  }

  return false;
}
